/** Hiring role UI helpers (deadlines, status labels, local settings). */

export const HIRING_ROLE_STATUSES = ["active", "hidden", "closed"];

/** Map API job post to workspace/dashboard status (active / hidden / closed). */
export function hiringUiStatusFromJobPost(jobPost) {
  if (!jobPost || typeof jobPost !== "object") return "active";
  const s = String(jobPost.status || "").trim().toLowerCase();
  if (s === "archived") return "closed";
  if (s === "published" && jobPost.listedInApplicantSearch === false) return "hidden";
  return "active";
}

function getLocalDateStringFromDate(d = new Date()) {
  const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return x.toISOString().slice(0, 10);
}

/** Today (local) as YYYY-MM-DD — used for deadline inputs / labels. */
export function getLocalDateString(d = new Date()) {
  return getLocalDateStringFromDate(d);
}

export function daysFromToday(days) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return getLocalDateStringFromDate(d);
}

/** Display helper for deadline chips / hints. */
export function formatRoleDateDisplay(iso) {
  if (!iso || typeof iso !== "string") return "";
  const parts = iso.split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return iso;
  const [y, m, day] = parts;
  const dt = new Date(y, m - 1, day);
  return dt.toLocaleDateString(undefined, { dateStyle: "medium" });
}

/** Display helper for role expiry hints (date + end-of-day UTC). */
export function formatRoleDeadlineDisplay(iso) {
  if (!iso || typeof iso !== "string") return "";
  const trimmed = iso.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return formatRoleDateDisplay(iso);
  const endsAtUtcMs = Date.parse(`${trimmed}T23:59:59.999Z`);
  if (!Number.isFinite(endsAtUtcMs)) return formatRoleDateDisplay(iso);
  return new Date(endsAtUtcMs).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
}

/** Short expiry line for role workspace subtitle (date + end-of-day UTC). */
export function formatRoleDeadlineSubtitle(iso) {
  return formatRoleDeadlineDisplay(iso);
}

/** Published timestamp for role workspace subtitle. */
export function formatRolePublishedSubtitle(iso) {
  if (iso == null || iso === "") return "";
  const d = iso instanceof Date ? iso : new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
}

export function hiringRoleStatusLabel(status) {
  switch (status) {
    case "active":
      return "Active";
    case "hidden":
      return "Hidden";
    case "closed":
      return "Closed";
    case "draft":
      return "Drafts";
    default:
      return status;
  }
}

const roleSettingsKey = (roleId) => `jobuwin-hiring-role-settings-${roleId}`;

/** @param {{ id: string, status: string, expiryDate?: string | null }} role */
export function loadHiringRoleSettings(role) {
  const base = {
    roleStatus: role.status,
    expiryDate: role.expiryDate || getLocalDateString(),
    closedSaved: role.status === "closed",
  };
  const raw = window.localStorage.getItem(roleSettingsKey(role.id));
  if (!raw) return base;
  try {
    const parsed = JSON.parse(raw);
    return {
      roleStatus: typeof parsed.roleStatus === "string" ? parsed.roleStatus : base.roleStatus,
      expiryDate: typeof parsed.expiryDate === "string" ? parsed.expiryDate : base.expiryDate,
      closedSaved: typeof parsed.closedSaved === "boolean" ? parsed.closedSaved : base.closedSaved,
    };
  } catch {
    return base;
  }
}

export function saveHiringRoleSettings(roleId, data) {
  window.localStorage.setItem(roleSettingsKey(roleId), JSON.stringify(data));
}
